import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useClients } from '../api/queries';
import type { LigneClient } from '../api/types';
import { Carte } from '../components/Carte';
import { Saisie, Selecteur } from '../components/Champs';
import { MessageErreur } from '../components/MessageErreur';
import { Tableau, type Colonne } from '../components/Tableau';
import { VALEUR_ABSENTE } from '../i18n/fr';
import { formaterNombre, formaterRelatif } from '../lib/format';
import { useDifferer } from '../lib/useDifferer';

/**
 * Vue CLIENT d'une exploitation, par opposition à la vue technique de `ExploitationsPage`.
 *
 * La différence n'est pas cosmétique : cet écran répond à « qui sont nos clients, que
 * possèdent-ils, que consomment-ils, que paient-ils, et lesquels vont mal », c'est-à-dire aux
 * questions d'un dirigeant ou d'un commercial. L'écran Exploitations répond à « quel est l'état
 * de ce parc », qui est la question d'un support.
 *
 * Les deux vivent côte à côte plutôt que fusionnées : une seule table à douze colonnes ne sert
 * bien aucun des deux métiers.
 */

const OPTIONS_ABONNEMENT = [
  { valeur: 'ACTIF', libelle: 'Abonnés (actif)' },
  { valeur: 'ESSAI', libelle: 'En période d’essai' },
  { valeur: 'IMPAYE', libelle: 'Impayés' },
  { valeur: 'SUSPENDU', libelle: 'Suspendus' },
  { valeur: 'AUCUN', libelle: 'Sans abonnement' },
];

/** Volume lisible. Base 1024 : c'est celle des forfaits de données. */
function formaterOctets(octets: number): string {
  if (octets <= 0) return '0';
  if (octets >= 1_073_741_824) return `${(octets / 1_073_741_824).toFixed(2)} Go`;
  if (octets >= 1_048_576) return `${(octets / 1_048_576).toFixed(1)} Mo`;
  if (octets >= 1024) return `${(octets / 1024).toFixed(1)} Ko`;
  return `${octets} o`;
}

/** Montant en centimes vers euros. La conversion n'a lieu qu'à l'affichage. */
export function formaterEuros(cents: number): string {
  return `${(cents / 100).toFixed(2)} €`;
}

export function ClientsPage(): JSX.Element {
  const naviguer = useNavigate();
  const [parametres, setParametres] = useSearchParams();

  const q = parametres.get('q') ?? '';
  const abonnement = parametres.get('abonnement') ?? '';

  const [recherche, setRecherche] = useState(q);
  const rechercheDifferee = useDifferer(recherche.trim());

  const filtres = useMemo(
    () => ({
      ...(rechercheDifferee ? { q: rechercheDifferee } : {}),
      ...(abonnement ? { abonnement } : {}),
    }),
    [rechercheDifferee, abonnement],
  );

  const { data, error, isPending } = useClients(filtres);

  const majFiltre = (cle: string, valeur: string): void => {
    const suivants = new URLSearchParams(parametres);
    if (valeur === '') suivants.delete(cle);
    else suivants.set(cle, valeur);
    setParametres(suivants, { replace: true });
  };

  const totaux = useMemo(() => {
    const items = data?.items ?? [];
    return {
      clients: items.length,
      appareils: items.reduce((s, c) => s + c.nb_electrificateurs, 0),
      critiques: items.reduce((s, c) => s + c.nb_appareils_critiques, 0),
      octets: items.reduce((s, c) => s + c.octets_30j, 0),
      revenu: items.reduce(
        (s, c) => s + (c.abonnement?.statut === 'ACTIF' ? c.abonnement.prix_mensuel_cents : 0),
        0,
      ),
      sav: items.reduce((s, c) => s + c.sav_ouverts, 0),
    };
  }, [data]);

  const colonnes: Colonne<LigneClient>[] = [
    {
      cle: 'nom',
      entete: 'Client',
      rendu: (c) => (
        <div>
          <div style={{ fontWeight: 600 }}>{c.nom}</div>
          <div style={{ fontSize: 12, color: '#6B7480' }}>
            {c.administrateur_email ?? VALEUR_ABSENTE}
          </div>
        </div>
      ),
    },
    {
      cle: 'utilisateurs',
      entete: 'Utilisateurs',
      numerique: true,
      rendu: (c) => formaterNombre(c.nb_utilisateurs),
    },
    {
      cle: 'appareils',
      entete: 'Appareils',
      numerique: true,
      rendu: (c) => (
        <div>
          <div>{formaterNombre(c.nb_electrificateurs)}</div>
          {c.nb_appareils_critiques > 0 || c.nb_appareils_hors_ligne > 0 ? (
            <div style={{ fontSize: 12 }}>
              {c.nb_appareils_critiques > 0 ? (
                <span style={{ color: '#D0342C' }}>{c.nb_appareils_critiques} crit.</span>
              ) : null}
              {c.nb_appareils_critiques > 0 && c.nb_appareils_hors_ligne > 0 ? ' · ' : ''}
              {c.nb_appareils_hors_ligne > 0 ? (
                <span style={{ color: '#6B7480' }}>{c.nb_appareils_hors_ligne} h.l.</span>
              ) : null}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      cle: 'donnees',
      entete: 'Données 30 j',
      numerique: true,
      rendu: (c) => (
        <div>
          <div>{formaterOctets(c.octets_30j)}</div>
          {c.nb_electrificateurs > 0 ? (
            <div style={{ fontSize: 12, color: '#6B7480' }}>
              {formaterOctets(Math.round(c.octets_30j / c.nb_electrificateurs))} / appareil
            </div>
          ) : null}
        </div>
      ),
    },
    {
      cle: 'abonnement',
      entete: 'Option SMS',
      rendu: (c) =>
        c.abonnement === null ? (
          <span style={{ color: '#6B7480' }}>—</span>
        ) : (
          <div>
            <div style={{ fontWeight: 600 }}>{c.abonnement.formule}</div>
            <div style={{ fontSize: 12, color: '#6B7480' }}>
              {c.abonnement.quota_mensuel === null
                ? 'illimité'
                : `${c.abonnement.sms_consommes_mois}/${c.abonnement.quota_mensuel} SMS`}
              {' · '}
              {formaterEuros(c.abonnement.prix_mensuel_cents)}
            </div>
          </div>
        ),
    },
    {
      cle: 'sav',
      entete: 'SAV',
      numerique: true,
      rendu: (c) =>
        c.sav_ouverts === 0 ? (
          <span style={{ color: '#6B7480' }}>—</span>
        ) : (
          <span style={{ color: '#ED7D0E', fontWeight: 600 }}>{c.sav_ouverts}</span>
        ),
    },
    {
      cle: 'activite',
      entete: 'Dernière trame',
      rendu: (c) => formaterRelatif(c.derniere_activite_at),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <h1 style={{ margin: 0 }}>Clients</h1>

      {/* Bandeau de synthèse : les six chiffres qu'on vient chercher en ouvrant cet écran. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12,
        }}
      >
        <Tuile libelle="Clients" valeur={formaterNombre(totaux.clients)} />
        <Tuile libelle="Appareils déployés" valeur={formaterNombre(totaux.appareils)} />
        <Tuile
          libelle="Appareils critiques"
          valeur={formaterNombre(totaux.critiques)}
          accent={totaux.critiques > 0 ? '#D0342C' : undefined}
        />
        <Tuile libelle="Données 30 j" valeur={formaterOctets(totaux.octets)} />
        <Tuile libelle="Revenu SMS / mois" valeur={formaterEuros(totaux.revenu)} accent="#16733A" />
        <Tuile
          libelle="SAV ouverts"
          valeur={formaterNombre(totaux.sav)}
          accent={totaux.sav > 0 ? '#ED7D0E' : undefined}
        />
      </div>

      <Carte>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Saisie
              label="Recherche"
              placeholder="Nom du client"
              value={recherche}
              onChange={(e) => {
                setRecherche(e.currentTarget.value);
                majFiltre('q', e.currentTarget.value.trim());
              }}
            />
          </div>
          <Selecteur
            label="Option SMS"
            optionVide="Tous les clients"
            options={OPTIONS_ABONNEMENT}
            value={abonnement}
            onChange={(e) => {
              majFiltre('abonnement', e.currentTarget.value);
            }}
          />
        </div>

        {error ? <MessageErreur erreur={error} /> : null}

        <Tableau
          colonnes={colonnes}
          lignes={data?.items ?? []}
          cleDe={(c) => c.id}
          chargement={isPending}
          titreVide="Aucun client"
          descriptionVide="Aucun client ne correspond à ces filtres."
          surClic={(c) => {
            naviguer(`/exploitations/${c.id}`);
          }}
        />
      </Carte>
    </div>
  );
}

/** Tuile de synthèse. Locale à cet écran : elle n'a pas encore d'autre usage. */
function Tuile({
  libelle,
  valeur,
  accent,
}: {
  libelle: string;
  valeur: string;
  accent?: string;
}): JSX.Element {
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E3E7EC',
        borderRadius: 12,
        padding: '14px 16px',
      }}
    >
      <div style={{ fontSize: 12, color: '#6B7480', textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {libelle}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: accent ?? '#14181F', marginTop: 4 }}>
        {valeur}
      </div>
    </div>
  );
}

export { formaterOctets };
